import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    fetchOrgAdmins,
    issueOrgAdmin,
    OrgAdminApiError,
    type OrgAdminIssued,
} from '@/api/orgAdmin';
import {
    Button,
    DataTable,
    EmptyState,
    ErrorState,
    FormField,
    Input,
    Modal,
    SectionCard,
    SkeletonTable,
    useToast,
    type ColumnConfig,
} from '@/components/ui';
import type { OrgAdminRow } from '@/api/orgAdmin';
import styles from './OrgDetail.module.css';

interface OrgAdminsTabProps {
    orgId: string;
    /** Issuing a NEW org-admin account is super-admin only (D-09,
     * `OrganizationAdminsView.post`) — org-admins may view this list for
     * their own org but never grant a peer. */
    isSuperAdmin: boolean;
}

/**
 * EAA-1815: `/organizations/:id?tab=admins` — full quản trị viên management.
 * Logic unchanged from the pre-redesign detail page (temp-password shown
 * exactly once, D12).
 */
export function OrgAdminsTab({ orgId, isSuperAdmin }: OrgAdminsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [adminForm, setAdminForm] = useState({ email: '', username: '' });
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [adminFormError, setAdminFormError] = useState<string | null>(null);
    const [issued, setIssued] = useState<OrgAdminIssued | null>(null);

    const adminsQuery = useQuery({
        queryKey: ['organization-admins', orgId, accessToken],
        queryFn: () => fetchOrgAdmins(config.apiBase, accessToken as string, orgId),
        retry: false,
    });
    const admins = adminsQuery.data ?? [];

    const adminColumns: ColumnConfig<OrgAdminRow>[] = useMemo(
        () => [
            {
                key: 'user_email',
                title: t('admin.organizations.adminsTab.columnEmail'),
                dataIndex: 'user_email',
                render: (_value, admin) => admin.user_email || admin.user_sub,
            },
            {
                key: 'granted_at',
                title: t('admin.organizations.adminsTab.columnGrantedAt'),
                dataIndex: 'granted_at',
                render: (value) => new Date(value as string).toLocaleString('vi-VN'),
            },
        ],
        [t]
    );

    const issueAdminMutation = useMutation({
        mutationFn: () =>
            issueOrgAdmin(config.apiBase, accessToken as string, orgId, {
                email: adminForm.email.trim(),
                username: adminForm.username.trim(),
            }),
        onSuccess: (data) => {
            setIssued(data);
            setAdminForm({ email: '', username: '' });
            setAdminFormError(null);
            setAdminModalOpen(false);
            toast.success(t('admin.organizations.adminsTab.toastIssued', { username: data.username }));
            void queryClient.invalidateQueries({ queryKey: ['organization-admins', orgId] });
        },
        onError: (error) => {
            setAdminFormError(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('admin.organizations.adminsTab.genericIssueError')
            );
        },
    });

    const onIssueAdmin = (event: FormEvent) => {
        event.preventDefault();
        if (!adminForm.email.trim() || !adminForm.username.trim()) {
            setAdminFormError(t('admin.organizations.adminsTab.form.validationError'));
            return;
        }
        issueAdminMutation.mutate();
    };

    const openAdminModal = () => {
        setAdminFormError(null);
        setAdminModalOpen(true);
    };
    const closeAdminModal = () => {
        if (issueAdminMutation.isPending) return;
        setAdminModalOpen(false);
    };

    return (
        <div className={styles.main}>
            <SectionCard
                title={t('admin.organizations.adminsTab.sectionTitle', { count: admins.length })}
                extra={
                    // EAA-1804: kit `Button` instead of hand-written
                    // `<button className="portal-btn portal-btn-primary">` (both here and the
                    // matching empty-state action below).
                    isSuperAdmin && (
                        <Button type="primary" onClick={openAdminModal}>
                            {t('admin.organizations.adminsTab.issueButton')}
                        </Button>
                    )
                }
            >
                {adminsQuery.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingSrOnly')}
                        </span>
                        <SkeletonTable rows={2} columns={2} />
                    </div>
                )}
                {adminsQuery.isError && (
                    <ErrorState
                        description={t('admin.organizations.adminsTab.loadErrorDescription')}
                        onRetry={() => void adminsQuery.refetch()}
                        retrying={adminsQuery.isFetching}
                    />
                )}
                {adminsQuery.isSuccess && admins.length === 0 && (
                    <EmptyState
                        title={t('admin.organizations.adminsTab.emptyTitle')}
                        action={
                            isSuperAdmin && (
                                <Button type="primary" onClick={openAdminModal}>
                                    {t('admin.organizations.adminsTab.emptyAction')}
                                </Button>
                            )
                        }
                    />
                )}
                {/* EAA-1804: hand-rolled `<table className="portal-admin-table">` replaced
                    with kit `DataTable` — the 2-column proof-of-concept for this pass (see
                    `AppsTable.tsx` for the reference column/actions shape). No pagination/sort
                    on this list (it never grew unbounded — org-admin lists are small), so
                    `pagination={false}` and no `sorter` on either column, matching the
                    hand-rolled version's behaviour exactly. */}
                {admins.length > 0 && (
                    <div className="portal-table-scroll">
                        <DataTable<OrgAdminRow>
                            data={admins}
                            rowKey="id"
                            pagination={false}
                            columns={adminColumns}
                        />
                    </div>
                )}

                {issued && (
                    <div className="portal-admin-secret" role="alert">
                        <strong>{t('admin.organizations.adminsTab.issuedBanner.title')}</strong>
                        <div>
                            {t('admin.organizations.adminsTab.issuedBanner.usernameLabel')}{' '}
                            <code>{issued.username}</code>
                        </div>
                        {issued.temp_password ? (
                            <div>
                                {t('admin.organizations.adminsTab.issuedBanner.tempPasswordLabel')}{' '}
                                <code>{issued.temp_password}</code>{' '}
                                {t('admin.organizations.adminsTab.issuedBanner.tempPasswordNote')}
                            </div>
                        ) : (
                            <div>
                                {t(
                                    'admin.organizations.adminsTab.issuedBanner.existingAccountNote'
                                )}
                            </div>
                        )}
                        {/* EAA-1804: this had no `portal-btn` class of its own — it inherited
                            the skin only through the legacy `.portal-admin-secret button:not(.ant-btn)`
                            selector (01-controls.css). Swapping to kit `Button` keeps the same
                            look, now sourced from the antd theme instead of that selector. */}
                        <Button onClick={() => setIssued(null)}>
                            {t('admin.organizations.adminsTab.issuedBanner.ackButton')}
                        </Button>
                    </div>
                )}
            </SectionCard>

            {isSuperAdmin && (
                <Modal
                    open={adminModalOpen}
                    onCancel={closeAdminModal}
                    title={t('admin.organizations.adminsTab.modal.title')}
                    footer={
                        <>
                            <Button onClick={closeAdminModal} disabled={issueAdminMutation.isPending}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form="issue-admin-form"
                                disabled={issueAdminMutation.isPending}
                            >
                                {issueAdminMutation.isPending
                                    ? t('admin.organizations.adminsTab.modal.submitPending')
                                    : t('admin.organizations.adminsTab.emptyAction')}
                            </Button>
                        </>
                    }
                >
                    {/* EAA-1804: hand-rolled `<label>` field chrome → kit `FormField`/`Input`.
                        `adminFormError` stays a form-level banner below both fields (it is not
                        tied to one field), so it keeps its own `<p>`, not a `FormField` error. */}
                    <form id="issue-admin-form" className="portal-admin-form" onSubmit={onIssueAdmin}>
                        <FormField label={t('admin.organizations.adminsTab.columnEmail')}>
                            <Input
                                type="email"
                                value={adminForm.email}
                                onChange={(e) =>
                                    setAdminForm({ ...adminForm, email: e.target.value })
                                }
                                required
                            />
                        </FormField>
                        <FormField label={t('admin.organizations.adminsTab.form.usernameLabel')}>
                            <Input
                                value={adminForm.username}
                                onChange={(e) =>
                                    setAdminForm({ ...adminForm, username: e.target.value })
                                }
                                required
                            />
                        </FormField>
                        {adminFormError && <p className="portal-admin-error">{adminFormError}</p>}
                    </form>
                </Modal>
            )}
        </div>
    );
}

export default OrgAdminsTab;
